import { Link, useParams } from "react-router-dom";
import { JobProgress } from "@/components/job-progress";
import { ResultRenderer } from "@/components/result-renderer";
import { ReviewsSection } from "@/components/reviews-section";
import { useJob } from "@/hooks/use-jobs";
import { formatTokens, relativeTime } from "@/lib/utils";

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: job, isLoading, error } = useJob(id ?? "");

  if (isLoading) {
    return (
      <div className="mx-auto mt-16 max-w-3xl px-4">
        <div className="h-8 w-1/2 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
        <div className="mt-6 h-24 animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-900" />
      </div>
    );
  }
  if (error || !job) {
    return (
      <div className="mx-auto mt-24 max-w-xl px-4 text-center">
        <h1 className="text-xl font-semibold">Job not found</h1>
        <Link
          to="/jobs"
          className="mt-6 inline-block text-sm text-neutral-500 hover:underline"
        >
          ← Back to your jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/jobs" className="text-sm text-neutral-500 hover:underline">
        ← Your jobs
      </Link>

      <header className="mt-4">
        <p className="font-mono text-xs text-neutral-500">{job.job_id}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {job.outcome_id}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Started {relativeTime(job.created_at)}
        </p>
      </header>

      <section className="mt-8">
        <JobProgress job={job} />
      </section>

      {job.status === "completed" && job.output ? (
        <section className="mt-8 rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
          <h2 className="text-sm font-medium">Result</h2>
          <div className="mt-4">
            <ResultRenderer output={job.output} />
          </div>
        </section>
      ) : null}

      <section className="mt-8 grid grid-cols-2 gap-4 text-sm">
        <div className="rounded-lg bg-neutral-50 p-4 dark:bg-neutral-900">
          <p className="text-xs text-neutral-500">Price held</p>
          <p className="mt-1 font-medium">
            {formatTokens(job.price_tokens)} tokens
          </p>
        </div>
        <div className="rounded-lg bg-neutral-50 p-4 dark:bg-neutral-900">
          <p className="text-xs text-neutral-500">Captured</p>
          <p className="mt-1 font-medium">
            {job.captured_amount !== null
              ? `${formatTokens(job.captured_amount)} tokens`
              : "—"}
          </p>
        </div>
        {job.refunded_amount > 0 ? (
          <div className="col-span-2 rounded-lg bg-violet-50 p-4 text-violet-800 dark:bg-violet-950 dark:text-violet-200">
            <p className="text-xs">Refunded</p>
            <p className="mt-1 font-medium">
              {formatTokens(job.refunded_amount)} tokens back to your wallet
            </p>
          </div>
        ) : null}
      </section>

      {job.status === "completed" ? (
        <div className="mt-12">
          <ReviewsSection
            outcomeId={job.outcome_id}
            canReview
            jobId={job.job_id}
          />
        </div>
      ) : null}
    </div>
  );
}
